import { useState } from 'react'

import { SearchInput, SegmentedControl } from '@/components/ui'
import { isCashShown } from '../AllAccountsCard/heroText'
import type { AllAccountsAsOf } from '../model/portfolioOverview'
import { longDate, sinceText } from '../portfolioText'
import { AccountRow } from './AccountRow'
import {
  type AccountSort,
  type AccountSortKey,
  DEFAULT_SORT,
  GROUP_PAST,
  PAGE_SIZE,
  pageOfLines,
  SEARCH_PAST,
  type TableLine,
  tableLines,
} from './accountLines'
import { GroupRow } from './GroupRow'
import { SortHeader } from './SortHeader'
import { TablePager } from './TablePager'

interface AccountsTableProps {
  asOf: AllAccountsAsOf
  selectedAccountId: string | null
  onSelectAccount: (accountId: string) => void
}

type Layout = 'grouped' | 'list'

const LAYOUT_OPTIONS: { value: Layout; label: string }[] = [
  { value: 'grouped', label: 'By institution' },
  { value: 'list', label: 'List' },
]

const nextSort = (current: AccountSort, key: AccountSortKey): AccountSort =>
  current.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: key === 'name' ? 'asc' : 'desc' }

const withToggled = (groups: Set<string>, institution: string) => {
  const next = new Set(groups)
  if (next.has(institution)) next.delete(institution)
  else next.add(institution)
  return next
}

const valueHeader = (asOf: AllAccountsAsOf) =>
  `${isCashShown(asOf) ? 'Securities and cash' : 'Securities'} on ${longDate(asOf.asOfDate)}`

const changeHeader = (asOf: AllAccountsAsOf) => {
  const since = asOf.accounts.find((account) => account.change)?.change?.since
  return since ? `Change since ${sinceText(since, asOf.asOfDate)}` : null
}

const lineKey = (line: TableLine) =>
  line.kind === 'group'
    ? `group-${line.group.institution}`
    : `account-${line.account.accountId}`

// The main block: one row per account on the as-of date. A click on a row chooses that account.
export const AccountsTable = ({
  asOf,
  selectedAccountId,
  onSelectAccount,
}: AccountsTableProps) => {
  const [sort, setSort] = useState<AccountSort>(DEFAULT_SORT)
  const [search, setSearch] = useState('')
  const [layout, setLayout] = useState<Layout>('grouped')
  const [toggledGroups, setToggledGroups] = useState<Set<string>>(new Set())
  const [pageIndex, setPageIndex] = useState(0)

  const { accounts } = asOf
  const canGroup = accounts.length > GROUP_PAST
  const hasSearch = accounts.length > SEARCH_PAST
  const changeLabel = changeHeader(asOf)
  const lines = tableLines({
    accounts,
    sort,
    search,
    grouped: canGroup && layout === 'grouped',
    toggledGroups,
    selectedAccountId,
  })
  const page = lines.length > PAGE_SIZE ? pageOfLines(lines, pageIndex) : null
  const visibleLines = page?.lines ?? lines

  const searchFor = (text: string) => {
    setSearch(text)
    setPageIndex(0)
  }
  const toggleGroup = (institution: string) =>
    setToggledGroups((groups) => withToggled(groups, institution))
  const sortBy = (key: AccountSortKey) =>
    setSort((current) => nextSort(current, key))

  return (
    <section className="rounded-lg border bg-white px-6 pt-6 pb-3 text-sm text-blue-7">
      <div className="flex min-h-8 flex-wrap items-center justify-between gap-4">
        <h3 className="text-base font-semibold">Accounts</h3>
        <div className="flex items-center gap-2">
          {hasSearch && (
            <SearchInput
              value={search}
              onChange={searchFor}
              placeholder="Search accounts..."
              className="w-[220px]"
            />
          )}
          {canGroup && (
            <SegmentedControl
              options={LAYOUT_OPTIONS}
              value={layout}
              onChange={setLayout}
              ariaLabel="Group accounts"
            />
          )}
        </div>
      </div>
      <div className="-mx-3 mt-5 overflow-x-auto">
        <table className="w-full min-w-[760px] table-fixed border-collapse text-sm">
          <colgroup>
            <col />
            <col className="w-[140px]" />
            <col className="w-[150px]" />
            {changeLabel && <col className="w-[132px]" />}
            <col className="w-[132px]" />
            <col className="w-[212px]" />
          </colgroup>
          <thead>
            <tr>
              <SortHeader
                label="Account"
                sortKey="name"
                sort={sort}
                onSort={sortBy}
              />
              <SortHeader
                label="Statements"
                sortKey="statements"
                sort={sort}
                onSort={sortBy}
              />
              <SortHeader
                label={valueHeader(asOf)}
                sortKey="value"
                sort={sort}
                onSort={sortBy}
                alignRight
              />
              {changeLabel && (
                <SortHeader
                  label={changeLabel}
                  sortKey="change"
                  sort={sort}
                  onSort={sortBy}
                  alignRight
                />
              )}
              <SortHeader
                label="Net money in and out"
                sortKey="netMoney"
                sort={sort}
                onSort={sortBy}
                alignRight
              />
              <th className="border-b" aria-label="Verdict" />
            </tr>
          </thead>
          <tbody className="[&>tr+tr>td]:border-t">
            {visibleLines.map((line) =>
              line.kind === 'group' ? (
                <GroupRow
                  key={lineKey(line)}
                  group={line.group}
                  isOpen={line.isOpen}
                  showChange={changeLabel != null}
                  onToggle={toggleGroup}
                />
              ) : (
                <AccountRow
                  key={lineKey(line)}
                  account={line.account}
                  inGroup={line.inGroup}
                  showChange={changeLabel != null}
                  isSelected={line.account.accountId === selectedAccountId}
                  onSelect={onSelectAccount}
                />
              ),
            )}
            {visibleLines.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-3 py-6 text-center text-ink-subtle"
                >
                  No accounts match "{search}".
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {page && <TablePager page={page} onPageChange={setPageIndex} />}
    </section>
  )
}
